/**
 * Pivot.is — Cortex Network Visualization
 * --------------------------------------
 * 3D network graph showing cortex relationships and activation flow.
 * 
 * Features:
 * - Nodes for each cortex positioned in 3D space
 * - Edges based on dependencies (required and optional)
 * - Pulsing animations along edges following activation flow
 * - Visual representation of data flow between cortices
 * - Real-time status updates from cortex execution
 */

import type * as THREE_NS from 'three';
import { cortexStatusManager, type CortexStatus, type CortexStatusData } from '../cortexStatusManager.js';

// Prism shader for glass cubes with chromatic dispersion
const prismVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vWorldPosition;
  
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = position;
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const createPrismFragmentShader = (includeLogo: boolean = false) => {
  const logoUniform = includeLogo ? 'uniform sampler2D uLogoTexture;\n  ' : '';
  return `
  uniform vec3 uTintColor;
  uniform float uTime;
  uniform vec3 uCameraPosition;
  uniform sampler2D uBackgroundTexture;
  ${logoUniform}uniform vec2 uResolution;
  uniform mat4 uProjectionMatrix;
  uniform mat4 uViewMatrix;
  
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vWorldPosition;
  
  // PS2-style refractive texture mapping
  // Simplified refraction coefficient (0.5-1.0 range, lower = more distortion)
  const float refractionCoeff = 0.7;
  
  // Chromatic dispersion - different refraction for RGB channels (reduced for subtler effect)
  const float iorRed = 1.05;
  const float iorGreen = 1.08;
  const float iorBlue = 1.1;
  
  // Calculate refracted ray approximation (PS2-style, avoiding expensive Snell's law)
  vec3 calculateRefractedRay(vec3 viewDir, vec3 normal, float coeff) {
    // Simplified: R = -(N * coeff + I)
    vec3 refracted = -(normal * coeff + viewDir);
    return normalize(refracted);
  }
  
  // Project refracted ray to screen space for UV offset
  vec2 projectRefractedUV(vec3 refractedRay, vec3 worldPos, mat4 proj, mat4 view) {
    // Project world position to screen space
    vec4 clipPos = proj * view * vec4(worldPos, 1.0);
    vec2 screenUV = (clipPos.xy / clipPos.w) * 0.5 + 0.5;
    
    // Offset based on refracted ray (PS2-style distortion)
    // Reduced offset for subtler refraction effect
    vec2 offset = refractedRay.xy * 0.015; // Reduced from 0.03 for less refraction
    return screenUV + offset;
  }
  
  void main() {
    vec3 viewDir = normalize(uCameraPosition - vWorldPosition);
    vec3 normal = normalize(vNormal);
    
    // Calculate refracted rays for each color channel (chromatic dispersion)
    vec3 refractedRed = calculateRefractedRay(viewDir, normal, iorRed);
    vec3 refractedGreen = calculateRefractedRay(viewDir, normal, iorGreen);
    vec3 refractedBlue = calculateRefractedRay(viewDir, normal, iorBlue);
    
    // Project to screen space and get UV offsets
    vec2 uvRed = projectRefractedUV(refractedRed, vWorldPosition, uProjectionMatrix, uViewMatrix);
    vec2 uvGreen = projectRefractedUV(refractedGreen, vWorldPosition, uProjectionMatrix, uViewMatrix);
    vec2 uvBlue = projectRefractedUV(refractedBlue, vWorldPosition, uProjectionMatrix, uViewMatrix);
    
    // Sample background texture with refracted UVs (chromatic dispersion)
    // This is the primary visual - the refracted background scene
    vec3 refractedColor = vec3(
      texture2D(uBackgroundTexture, clamp(uvRed, 0.0, 1.0)).r,
      texture2D(uBackgroundTexture, clamp(uvGreen, 0.0, 1.0)).g,
      texture2D(uBackgroundTexture, clamp(uvBlue, 0.0, 1.0)).b
    );
    
    // Fresnel effect for glass edges (subtle reflection)
    float fresnel = pow(1.0 - max(dot(viewDir, normal), 0.0), 2.5);
    
    // Pure refracted background - no tint, no glass color mixing
    // Only add subtle fresnel highlights at edges
    vec3 fresnelHighlight = vec3(0.6, 0.65, 0.7);
    vec3 color = mix(refractedColor, fresnelHighlight, fresnel * 0.15);
    
    // Glass transparency - more transparent in center, slightly more opaque at edges
    float alpha = 0.5 + fresnel * 0.2;
    
    ${includeLogo ? `
    // Sample logo texture and blend it into the sphere
    // Use spherical coordinates to map UV
    vec3 sphereNormal = normalize(vNormal);
    float logoU = atan(sphereNormal.x, sphereNormal.z) / (2.0 * 3.14159265359) + 0.5;
    float logoV = acos(sphereNormal.y) / 3.14159265359;
    vec2 logoUV = vec2(logoU, logoV);
    vec4 logoColor = texture2D(uLogoTexture, logoUV);
    
    // Blend logo with refracted background (logo appears inside the glass)
    // Use logo alpha to control visibility
    color = mix(color, logoColor.rgb, logoColor.a * 0.8);
    ` : ''}
    
    gl_FragColor = vec4(color, alpha);
  }
`;
};

// Inner cube fragment shader - simple, performant, visible
// Just adds a solid tint color overlay to make it stand out
const createInnerCubeFragmentShader = () => {
  return `
  uniform vec3 uTintColor;
  uniform float uTime;
  uniform vec3 uCameraPosition;
  uniform sampler2D uBackgroundTexture;
  uniform vec2 uResolution;
  uniform mat4 uProjectionMatrix;
  uniform mat4 uViewMatrix;
  
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vWorldPosition;
  
  // PS2-style refractive texture mapping (reduced for subtler effect)
  const float refractionCoeff = 0.7;
  const float iorRed = 1.05;
  const float iorGreen = 1.08;
  const float iorBlue = 1.1;
  
  vec3 calculateRefractedRay(vec3 viewDir, vec3 normal, float coeff) {
    vec3 refracted = -(normal * coeff + viewDir);
    return normalize(refracted);
  }
  
  vec2 projectRefractedUV(vec3 refractedRay, vec3 worldPos, mat4 proj, mat4 view) {
    vec4 clipPos = proj * view * vec4(worldPos, 1.0);
    vec2 screenUV = (clipPos.xy / clipPos.w) * 0.5 + 0.5;
    vec2 offset = refractedRay.xy * 0.015; // Reduced from 0.03 for less refraction
    return screenUV + offset;
  }
  
  void main() {
    vec3 viewDir = normalize(uCameraPosition - vWorldPosition);
    vec3 normal = normalize(vNormal);
    
    // Calculate refracted rays for chromatic dispersion
    vec3 refractedRed = calculateRefractedRay(viewDir, normal, iorRed);
    vec3 refractedGreen = calculateRefractedRay(viewDir, normal, iorGreen);
    vec3 refractedBlue = calculateRefractedRay(viewDir, normal, iorBlue);
    
    vec2 uvRed = projectRefractedUV(refractedRed, vWorldPosition, uProjectionMatrix, uViewMatrix);
    vec2 uvGreen = projectRefractedUV(refractedGreen, vWorldPosition, uProjectionMatrix, uViewMatrix);
    vec2 uvBlue = projectRefractedUV(refractedBlue, vWorldPosition, uProjectionMatrix, uViewMatrix);
    
    vec3 refractedColor = vec3(
      texture2D(uBackgroundTexture, clamp(uvRed, 0.0, 1.0)).r,
      texture2D(uBackgroundTexture, clamp(uvGreen, 0.0, 1.0)).g,
      texture2D(uBackgroundTexture, clamp(uvBlue, 0.0, 1.0)).b
    );
    
    // Simple fresnel for edges
    float fresnel = pow(1.0 - max(dot(viewDir, normal), 0.0), 2.5);
    
    // Add solid tint color overlay - makes inner cube clearly visible
    // Mix refracted color with tint color (30% tint, 70% refracted)
    vec3 tintedColor = mix(refractedColor, uTintColor, 0.3);
    
    vec3 fresnelHighlight = vec3(0.6, 0.65, 0.7);
    vec3 color = mix(tintedColor, fresnelHighlight, fresnel * 0.15);
    
    // Much more opaque to make it clearly visible (0.85 vs 0.5 for outer)
    float alpha = 0.85 + fresnel * 0.1;
    
    gl_FragColor = vec4(color, alpha);
  }
`;
};

// Cortex network structure based on actual dependencies
interface CortexNode {
  id: string;
  label: string;
  position: [number, number, number];
  wave: number; // Which wave this cortex belongs to
  category: 'foundation' | 'research' | 'analysis' | 'integration' | 'interface' | 'specialized' | 'health' | 'financial' | 'education' | 'technical';
  active: boolean; // Whether this cortex is currently implemented
}

interface CortexEdge {
  from: string;
  to: string;
  type: 'required' | 'optional';
}

// Scale factor for the network (increase to make it larger)
const NETWORK_SCALE = 2.2; // Increased for better spacing between nodes and edges

// ⚙️ STRESS TEST CONFIGURATION
// Set this to any number to add dummy cortices for performance testing
// Set to 0 to disable dummy cortices
const DUMMY_CORTEX_COUNT = 0; // Change this to 0, 10, 100, etc.

// Battery configuration for inactive cortices: 6 columns × 6 rows (36 positions, 37th in 7th row)
const INACTIVE_GRID_COLS = 4
const INACTIVE_GRID_ROWS = 4;
const INACTIVE_GRID_SPACING = 0.7 * NETWORK_SCALE; // Gap between cortices (doubled from 0.35)
const INACTIVE_BASE_X = -10 * NETWORK_SCALE; // Starting X position (left side)
const INACTIVE_BASE_Y = -12 * NETWORK_SCALE; // Starting Y position (below active network)
const INACTIVE_BASE_Z = -15 * NETWORK_SCALE; // Z position (behind active network)

// Dummy cortex grid configuration (for stress testing)
const DUMMY_GRID_COLS = 5; // 5 columns for dummy cortices
const DUMMY_GRID_SPACING = 0.6 * NETWORK_SCALE; // Slightly tighter spacing
const DUMMY_BASE_X = 12 * NETWORK_SCALE; // Starting X position (right side, opposite inactive)
const DUMMY_BASE_Y = -12 * NETWORK_SCALE; // Starting Y position (below active network, same as inactive)
const DUMMY_BASE_Z = -15 * NETWORK_SCALE; // Z position (behind active network)

// Helper function to calculate grid position for dummy cortices
function getDummyGridPos(index: number): [number, number, number] {
  const col = index % DUMMY_GRID_COLS;
  const row = Math.floor(index / DUMMY_GRID_COLS);
  const x = DUMMY_BASE_X + col * DUMMY_GRID_SPACING;
  const y = DUMMY_BASE_Y + row * DUMMY_GRID_SPACING;
  const z = DUMMY_BASE_Z;
  return [x, y, z];
}

// Helper function to calculate grid position for inactive cortices
function getInactiveGridPos(index: number): [number, number, number] {
  const col = index % INACTIVE_GRID_COLS;
  const row = Math.floor(index / INACTIVE_GRID_COLS);
  const x = INACTIVE_BASE_X + col * INACTIVE_GRID_SPACING;
  const y = INACTIVE_BASE_Y + row * INACTIVE_GRID_SPACING;
  const z = INACTIVE_BASE_Z;
  return [x, y, z];
}


// Define cortex nodes with positions and categories
// Flipped 180° upside down (Y positions negated)
export const CORTEX_NODES: CortexNode[] = [
  // Wave 0: Entry point (always active)
  { id: 'conversational', label: 'Conversational', position: [0, 0, 0], wave: 0, category: 'interface', active: true },

  // Wave 1: Foundation + Meta-Questioning
  { id: 'survey', label: 'Survey', position: [-8 * NETWORK_SCALE, -4 * NETWORK_SCALE, -2 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },
  { id: 'intent', label: 'Intent', position: [0, -4 * NETWORK_SCALE, 0], wave: 1, category: 'foundation', active: true },
  { id: 'ttrp', label: 'TTRP', position: [-6 * NETWORK_SCALE, -4 * NETWORK_SCALE, -1 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },
  { id: 'meta', label: 'Meta', position: [-2 * NETWORK_SCALE, -4 * NETWORK_SCALE, 1 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },
  { id: 'objectivity', label: 'Objectivity', position: [2 * NETWORK_SCALE, -4 * NETWORK_SCALE, -1 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },
  { id: 'context', label: 'Context', position: [8 * NETWORK_SCALE, -4 * NETWORK_SCALE, 2 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },
  { id: 'memory', label: 'Memory', position: [-4 * NETWORK_SCALE, -4 * NETWORK_SCALE, 4 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },
  { id: 'activation_criteria', label: 'Activation Criteria', position: [4 * NETWORK_SCALE, -4 * NETWORK_SCALE, -4 * NETWORK_SCALE], wave: 1, category: 'foundation', active: true },

  // Wave 2: Research & Framework
  { id: 'framework', label: 'Framework', position: [-6 * NETWORK_SCALE, 0, -3 * NETWORK_SCALE], wave: 2, category: 'research', active: true },

  // Wave 3: Analysis
  { id: 'heuristic', label: 'Heuristic', position: [-8 * NETWORK_SCALE, 2 * NETWORK_SCALE, -1 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'empirical', label: 'Empirical', position: [-4 * NETWORK_SCALE, 2 * NETWORK_SCALE, 3 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'rational', label: 'Rational', position: [0, 3.5 * NETWORK_SCALE, 0], wave: 3, category: 'analysis', active: true },
  { id: 'predictive', label: 'Predictive', position: [4 * NETWORK_SCALE, 2 * NETWORK_SCALE, -3 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'reflective', label: 'Reflective', position: [8 * NETWORK_SCALE, 2 * NETWORK_SCALE, 1 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'emotional', label: 'Emotional', position: [-2 * NETWORK_SCALE, 2 * NETWORK_SCALE, 5 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'pragmatic', label: 'Pragmatic', position: [2 * NETWORK_SCALE, 2 * NETWORK_SCALE, -5 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'simulation', label: 'Simulation', position: [0, 2 * NETWORK_SCALE, 6 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },

  // Wave 4: Integration & Decision
  { id: 'integrator', label: 'Integrator', position: [0, 6 * NETWORK_SCALE, 0], wave: 4, category: 'integration', active: true },
  { id: 'decision', label: 'Decision', position: [0, 8 * NETWORK_SCALE, 0], wave: 4, category: 'integration', active: true },
  { id: 'proactive', label: 'Proactive', position: [0, -6 * NETWORK_SCALE, 0], wave: 1, category: 'interface', active: true },

  // Additional active cortices
  { id: 'quantitative', label: 'Quantitative', position: [-6 * NETWORK_SCALE, 2 * NETWORK_SCALE, 2 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'social', label: 'Social', position: [6 * NETWORK_SCALE, 2 * NETWORK_SCALE, -2 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'ethical', label: 'Ethical', position: [-2 * NETWORK_SCALE, 2 * NETWORK_SCALE, -6 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'meta_temporal', label: 'Meta Temporal', position: [-4 * NETWORK_SCALE, 2 * NETWORK_SCALE, -4 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'decision_criteria', label: 'Decision Criteria', position: [4 * NETWORK_SCALE, 2 * NETWORK_SCALE, 4 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'data', label: 'Data', position: [-10 * NETWORK_SCALE, 2 * NETWORK_SCALE, -8 * NETWORK_SCALE], wave: 2, category: 'research', active: true },
  { id: 'tabular_analysis', label: 'Tabular Analysis', position: [-10 * NETWORK_SCALE, 2 * NETWORK_SCALE, 0], wave: 3, category: 'analysis', active: true },
  { id: 'tabular_transform', label: 'Tabular Transform', position: [-10 * NETWORK_SCALE, 2 * NETWORK_SCALE, 4 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },
  { id: 'tabular_qa', label: 'Tabular QA', position: [-10 * NETWORK_SCALE, 2 * NETWORK_SCALE, -4 * NETWORK_SCALE], wave: 3, category: 'analysis', active: true },

  // INACTIVE CORTICES - Planned but not yet implemented
  // Organized in a battery-like grid structure with gaps between each
  // Positioned below the active network (negative Y) in a cohesive block

  // Personal Development (row 0)
  { id: 'honesty', label: 'Honesty', position: getInactiveGridPos(0), wave: 3, category: 'specialized', active: false },
  { id: 'identity', label: 'Identity', position: getInactiveGridPos(1), wave: 3, category: 'specialized', active: false },
  { id: 'wellbeing', label: 'Well-being', position: getInactiveGridPos(2), wave: 3, category: 'health', active: false },
  { id: 'happiness', label: 'Happiness', position: getInactiveGridPos(3), wave: 3, category: 'specialized', active: false },
  { id: 'quantified_self', label: 'Quantified Self', position: getInactiveGridPos(4), wave: 3, category: 'specialized', active: false },
  { id: 'motivation', label: 'Motivation', position: getInactiveGridPos(5), wave: 3, category: 'specialized', active: false },

  // Health & Wellness (row 1)
  { id: 'therapy', label: 'Therapy', position: getInactiveGridPos(6), wave: 3, category: 'health', active: false },
  { id: 'fitness', label: 'Fitness', position: getInactiveGridPos(7), wave: 3, category: 'health', active: false },
  { id: 'sleep', label: 'Sleep', position: getInactiveGridPos(8), wave: 3, category: 'health', active: false },
  { id: 'nutrition', label: 'Nutrition', position: getInactiveGridPos(9), wave: 3, category: 'health', active: false },
  { id: 'behavior_addiction', label: 'Behavior & Addiction', position: getInactiveGridPos(10), wave: 3, category: 'health', active: false },

  // Financial & Economic (row 2)
  { id: 'financial_advisor', label: 'Financial Advisor', position: getInactiveGridPos(11), wave: 3, category: 'financial', active: false },
  { id: 'economics', label: 'Economics', position: getInactiveGridPos(12), wave: 3, category: 'financial', active: false },
  { id: 'business', label: 'Business', position: getInactiveGridPos(13), wave: 3, category: 'financial', active: false },
  { id: 'investor', label: 'Investor', position: getInactiveGridPos(14), wave: 3, category: 'financial', active: false },
  { id: 'negotiator_contract', label: 'Negotiator/Contract', position: getInactiveGridPos(15), wave: 3, category: 'financial', active: false },
  { id: 'negotiator_diplomat', label: 'Negotiator Diplomat', position: getInactiveGridPos(16), wave: 3, category: 'financial', active: false },

  // Learning & Growth (row 3)
  { id: 'educational', label: 'Educational', position: getInactiveGridPos(17), wave: 3, category: 'education', active: false },
  { id: 'fullstack_education', label: 'Full-Stack Education', position: getInactiveGridPos(18), wave: 3, category: 'education', active: false },
  { id: 'career_services', label: 'Career Services', position: getInactiveGridPos(19), wave: 3, category: 'education', active: false },
  { id: 'genius', label: 'Genius', position: getInactiveGridPos(20), wave: 3, category: 'education', active: false },
  { id: 'first_principles', label: 'First Principles', position: getInactiveGridPos(21), wave: 3, category: 'education', active: false },

  // Creative & Alternative Thinking (row 4)
  { id: 'better_idea', label: 'Better Idea', position: getInactiveGridPos(22), wave: 3, category: 'specialized', active: false },
  { id: 'thinking_outside_box', label: 'Thinking Outside Box', position: getInactiveGridPos(23), wave: 3, category: 'specialized', active: false },
  { id: 'contrarian', label: 'Contrarian', position: getInactiveGridPos(24), wave: 3, category: 'specialized', active: false },
  { id: 'drawing', label: 'Drawing', position: getInactiveGridPos(25), wave: 3, category: 'specialized', active: false },

  // Technical & Professional (row 5)
  { id: 'software_development', label: 'Software Development', position: getInactiveGridPos(26), wave: 3, category: 'technical', active: false },
  { id: 'project_management', label: 'Project Management', position: getInactiveGridPos(27), wave: 3, category: 'technical', active: false },
  { id: 'entrepreneur', label: 'Entrepreneur', position: getInactiveGridPos(28), wave: 3, category: 'technical', active: false },
  { id: 'operating_system', label: 'Operating System', position: getInactiveGridPos(29), wave: 3, category: 'technical', active: false },
  { id: 'web_browser', label: 'Web Browser', position: getInactiveGridPos(30), wave: 3, category: 'technical', active: false },

  // System & Meta (row 6)
  { id: 'support', label: 'Support', position: getInactiveGridPos(31), wave: 3, category: 'specialized', active: false },
  { id: 'evolution', label: 'Evolution', position: getInactiveGridPos(32), wave: 3, category: 'specialized', active: false },

  // Specialized (row 6 continued)
  { id: 'trends', label: 'Trends', position: getInactiveGridPos(33), wave: 3, category: 'specialized', active: false },
  { id: 'parenting', label: 'Parenting', position: getInactiveGridPos(34), wave: 3, category: 'specialized', active: false },
  { id: 'privacy', label: 'Privacy', position: getInactiveGridPos(35), wave: 3, category: 'specialized', active: false },
  { id: 'culture', label: 'Culture', position: getInactiveGridPos(36), wave: 3, category: 'specialized', active: false },

  // Proactive Cortexes (row 7)
  { id: 'proactive_caring', label: 'Proactive Caring', position: getInactiveGridPos(37), wave: 3, category: 'specialized', active: false },
  { id: 'proactive_schedule', label: 'Proactive Schedule', position: getInactiveGridPos(38), wave: 3, category: 'specialized', active: false },
  { id: 'proactive_insights', label: 'Proactive Insights', position: getInactiveGridPos(39), wave: 3, category: 'specialized', active: false },
  { id: 'proactive_wellbeing', label: 'Proactive Well-being', position: getInactiveGridPos(40), wave: 3, category: 'health', active: false },

  // ⚙️ DUMMY CORTICES (for stress testing)
  // Generated programmatically based on DUMMY_CORTEX_COUNT
  ...Array.from({ length: DUMMY_CORTEX_COUNT }, (_, i) => ({
    id: `dummy_${i + 1}`,
    label: `Dummy ${i + 1}`,
    position: getDummyGridPos(i),
    wave: 1,
    category: 'specialized' as const,
    active: true, // Active so they render and stress test the system
  })),
];

// Get cortex dependencies from registry structure
export const getCortexDependencies = (cortexId: string): { required: string[]; optional: string[] } => {
  const deps: { required: string[]; optional: string[] } = { required: [], optional: [] };

  // Based on cortexRegistry.ts dependency definitions
  // Wave 1: Foundation + Meta-Questioning
  if (cortexId === 'ttrp') {
    deps.required = ['intent'];
    deps.optional = ['memory'];
  }
  if (cortexId === 'meta') {
    deps.required = ['intent'];
    deps.optional = ['ttrp'];
  }
  if (cortexId === 'objectivity') {
    deps.required = ['intent'];
    deps.optional = ['ttrp', 'meta'];
  }
  if (cortexId === 'emotional' || cortexId === 'pragmatic') {
    deps.required = ['intent'];
  }
  if (cortexId === 'integrator') {
    deps.required = ['heuristic', 'rational'];
    deps.optional = ['empirical', 'predictive', 'reflective'];
  }
  if (cortexId === 'decision') {
    deps.required = ['integrator'];
  }

  // Optional dependencies
  if (cortexId === 'heuristic') deps.optional = ['intent', 'framework'];
  if (cortexId === 'rational') deps.optional = ['heuristic', 'framework'];
  if (cortexId === 'empirical') deps.optional = ['intent', 'data'];
  if (cortexId === 'predictive') deps.optional = ['heuristic'];
  if (cortexId === 'reflective') deps.optional = ['rational'];
  if (cortexId === 'framework') deps.optional = ['intent', 'survey'];
  if (cortexId === 'simulation') deps.optional = ['heuristic', 'intent', 'predictive', 'pragmatic'];
  if (cortexId === 'emotional') deps.optional = ['meta', 'social'];
  if (cortexId === 'pragmatic') deps.optional = ['rational', 'predictive', 'social'];
  if (cortexId === 'activation_criteria') deps.optional = ['intent', 'heuristic', 'emotional', 'empirical', 'decision_criteria'];
  if (cortexId === 'data') {
    deps.required = ['intent'];
    deps.optional = ['context'];
  }
  if (cortexId === 'tabular_analysis') {
    deps.required = ['intent'];
    deps.optional = ['data', 'empirical'];
  }
  if (cortexId === 'tabular_transform') {
    deps.required = ['intent'];
    deps.optional = ['tabular_analysis', 'data'];
  }
  if (cortexId === 'tabular_qa') {
    deps.required = ['intent'];
    deps.optional = ['tabular_analysis', 'data'];
  }

  // ⚙️ DUMMY CORTICES (for stress testing)
  // Dummy cortices have no dependencies to keep them simple
  if (cortexId.startsWith('dummy_')) {
    // No dependencies - they're just for stress testing
  }

  return deps;
};

// Define edges based on dependencies
const CORTEX_EDGES: CortexEdge[] = [
  // Conversational connects to everything (entry point)
  { from: 'conversational', to: 'intent', type: 'required' },

  // Wave 1 dependencies - Foundation + Meta-Questioning
  { from: 'intent', to: 'ttrp', type: 'required' },
  { from: 'intent', to: 'meta', type: 'required' },
  { from: 'intent', to: 'objectivity', type: 'required' },
  { from: 'memory', to: 'ttrp', type: 'optional' },
  { from: 'ttrp', to: 'meta', type: 'optional' },
  { from: 'ttrp', to: 'objectivity', type: 'optional' },
  { from: 'meta', to: 'objectivity', type: 'optional' },
  { from: 'intent', to: 'emotional', type: 'required' },
  { from: 'intent', to: 'pragmatic', type: 'required' },
  { from: 'intent', to: 'heuristic', type: 'optional' },
  { from: 'intent', to: 'empirical', type: 'optional' },
  { from: 'intent', to: 'framework', type: 'optional' },
  { from: 'survey', to: 'framework', type: 'optional' },
  { from: 'meta', to: 'emotional', type: 'optional' },

  // Wave 2 dependencies
  { from: 'framework', to: 'heuristic', type: 'optional' },
  { from: 'framework', to: 'rational', type: 'optional' },

  // Wave 3 dependencies
  { from: 'heuristic', to: 'rational', type: 'optional' },
  { from: 'heuristic', to: 'predictive', type: 'optional' },
  { from: 'heuristic', to: 'simulation', type: 'optional' },
  { from: 'rational', to: 'reflective', type: 'optional' },
  { from: 'intent', to: 'simulation', type: 'optional' },
  { from: 'predictive', to: 'simulation', type: 'optional' },
  { from: 'pragmatic', to: 'simulation', type: 'optional' },
  { from: 'social', to: 'emotional', type: 'optional' },
  { from: 'social', to: 'pragmatic', type: 'optional' },
  { from: 'rational', to: 'pragmatic', type: 'optional' },
  { from: 'predictive', to: 'pragmatic', type: 'optional' },
  { from: 'intent', to: 'data', type: 'required' },
  { from: 'context', to: 'data', type: 'optional' },
  { from: 'intent', to: 'tabular_analysis', type: 'required' },
  { from: 'data', to: 'tabular_analysis', type: 'optional' },
  { from: 'empirical', to: 'tabular_analysis', type: 'optional' },
  { from: 'intent', to: 'tabular_transform', type: 'required' },
  { from: 'tabular_analysis', to: 'tabular_transform', type: 'optional' },
  { from: 'data', to: 'tabular_transform', type: 'optional' },
  { from: 'intent', to: 'tabular_qa', type: 'required' },
  { from: 'tabular_analysis', to: 'tabular_qa', type: 'optional' },
  { from: 'data', to: 'tabular_qa', type: 'optional' },
  { from: 'data', to: 'empirical', type: 'optional' },
  { from: 'tabular_analysis', to: 'empirical', type: 'optional' },
  { from: 'tabular_analysis', to: 'quantitative', type: 'optional' },
  { from: 'tabular_analysis', to: 'rational', type: 'optional' },
  { from: 'tabular_analysis', to: 'reflective', type: 'optional' },
  { from: 'tabular_transform', to: 'quantitative', type: 'optional' },
  { from: 'tabular_transform', to: 'rational', type: 'optional' },
  { from: 'tabular_transform', to: 'predictive', type: 'optional' },
  { from: 'tabular_qa', to: 'empirical', type: 'optional' },
  { from: 'tabular_qa', to: 'quantitative', type: 'optional' },
  { from: 'tabular_qa', to: 'rational', type: 'optional' },

  // Wave 4 dependencies
  { from: 'heuristic', to: 'integrator', type: 'required' },
  { from: 'rational', to: 'integrator', type: 'required' },
  { from: 'empirical', to: 'integrator', type: 'optional' },
  { from: 'predictive', to: 'integrator', type: 'optional' },
  { from: 'reflective', to: 'integrator', type: 'optional' },
  { from: 'integrator', to: 'decision', type: 'required' },

  // Back to conversational (summary)
  { from: 'decision', to: 'conversational', type: 'required' },
  { from: 'integrator', to: 'conversational', type: 'optional' },

  // ⚙️ DUMMY CORTICES (for stress testing)
  // Optionally connect dummy cortices to intent for visual stress testing
  ...Array.from({ length: DUMMY_CORTEX_COUNT }, (_, i) => ({
    from: 'intent',
    to: `dummy_${i + 1}`,
    type: 'optional' as const,
  })),
];

// Activation flow sequence (for pulsing animation)
const ACTIVATION_FLOW: string[] = [
  'conversational',
  'intent',
  'ttrp',
  'meta',
  'objectivity',
  'survey',
  'context',
  'memory',
  'activation_criteria',
  'framework',
  'heuristic',
  'empirical',
  'rational',
  'predictive',
  'reflective',
  'emotional',
  'pragmatic',
  'simulation',
  'quantitative',
  'social',
  'ethical',
  'meta_temporal',
  'decision_criteria',
  'data',
  'proactive',
  'tabular_analysis',
  'tabular_transform',
  'tabular_qa',
  'integrator',
  'decision',
  'conversational', // Final summary
  // ⚙️ DUMMY CORTICES (for stress testing)
  ...Array.from({ length: DUMMY_CORTEX_COUNT }, (_, i) => `dummy_${i + 1}`),
];

export interface CortexNetworkState {
  lastHoverSoundTime?: number; // Timestamp of last hover sound (for throttling)
  nodes: Map<string, THREE_NS.Mesh>;
  edges: THREE_NS.Line[];
  edgeSprites: Map<number, THREE_NS.Sprite>;
  pulseState: {
    activeEdge: number;
    pulseProgress: number;
    flowIndex: number;
  };
  raycaster: THREE_NS.Raycaster | null;
  tooltipElement: HTMLElement | null;
  hoveredNode: string | null;
  selectedNode: string | null;
  cameraState: {
    isZoomed: boolean;
    targetPosition: THREE_NS.Vector3 | null;
    originalCameraPosition: THREE_NS.Vector3 | null;
    originalCameraTarget: THREE_NS.Vector3 | null;
    originalCameraFov: number | null;
    originalCameraHeight: number | null; // Preserve original Y position for dynamic vertical perspective
    zoomStartTime: number | null;
    zoomStartPosition: THREE_NS.Vector3 | null;
    zoomStartTarget: THREE_NS.Vector3 | null;
    orbitStartTime: number | null;
    transitionToOrbitStartTime: number | null;
    transitionToOrbitStartPosition: THREE_NS.Vector3 | null;
  };
  backgroundRenderTarget: THREE_NS.WebGLRenderTarget | null;
  metaballBackgroundRenderTarget: THREE_NS.WebGLRenderTarget | null; // Separate render target for metaball (includes attention sphere)
  backgroundScene: THREE_NS.Scene | null;
  spriteTexture: THREE_NS.Texture | null;
}

// Cortex descriptions (one-line descriptions)
/**
 * Update visual appearance of a cortex node based on its status
 */
function updateNodeVisualState(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  cortexId: string,
  statusData: CortexStatusData
): void {
  const node = state.nodes.get(cortexId);
  if (!node) return;

  const material = node.material as any; // ShaderMaterial
  const innerMaterial = node.userData.innerMaterial;
  const baseTint = node.userData.tintRGB || [0, 0, 0];
  const baseScale = 1.0;

  // Status-based visual states
  let statusColor: [number, number, number] = [baseTint[0], baseTint[1], baseTint[2]];
  let opacity = 0.7;
  let emissiveIntensity = 1.0;
  let scale = baseScale;
  let pulseIntensity = 0;

  switch (statusData.status) {
    case 'idle':
      // Default appearance
      statusColor = [baseTint[0], baseTint[1], baseTint[2]];
      opacity = 0.7;
      emissiveIntensity = 1.0;
      scale = baseScale;
      break;

    case 'pending':
      // Orange pulsing glow
      statusColor = [1.0, 0.67, 0.0]; // #ffaa00
      opacity = 0.8;
      emissiveIntensity = 1.2;
      scale = baseScale * 1.05;
      pulseIntensity = 0.3;
      break;

    case 'waiting':
      // Yellow glow, highlight dependency edges
      statusColor = [1.0, 0.87, 0.0]; // #ffdd00
      opacity = 0.85;
      emissiveIntensity = 1.3;
      scale = baseScale * 1.08;
      pulseIntensity = 0.2;

      // Highlight edges to dependencies
      if (statusData.waitingFor && statusData.waitingFor.length > 0) {
        highlightDependencyEdges(state, THREE, cortexId, statusData.waitingFor);
      }
      break;

    case 'active':
      // Bright green glow
      statusColor = [0.0, 1.0, 0.53]; // #00ff88
      opacity = 0.9;
      emissiveIntensity = 1.5;
      scale = baseScale * 1.1;
      break;

    case 'blocked':
      // Dimmed red glow
      statusColor = [1.0, 0.27, 0.27]; // #ff4444
      opacity = 0.5;
      emissiveIntensity = 0.8;
      scale = baseScale * 0.95;
      break;

    case 'skipped':
      // Very dim gray
      statusColor = [0.4, 0.4, 0.4]; // #666666
      opacity = 0.3;
      emissiveIntensity = 0.5;
      scale = baseScale * 0.9;
      break;

    case 'completed':
      // Static green glow
      statusColor = [0.0, 1.0, 0.53]; // #00ff88
      opacity = 0.8;
      emissiveIntensity = 1.2;
      scale = baseScale;
      break;

    case 'failed':
      // Pulsing red glow
      statusColor = [1.0, 0.0, 0.0]; // #ff0000
      opacity = 0.7;
      emissiveIntensity = 1.3;
      scale = baseScale * 1.05;
      pulseIntensity = 0.4;
      break;
  }

  // Apply pulsing if needed
  if (pulseIntensity > 0) {
    const time = performance.now() * 0.001;
    const pulse = (Math.sin(time * 3) + 1) * 0.5; // 0 to 1
    opacity += pulse * pulseIntensity * 0.2;
    emissiveIntensity += pulse * pulseIntensity * 0.3;
    scale += pulse * pulseIntensity * 0.05;
  }

  // Update material uniforms
  if (material && material.uniforms) {
    material.uniforms.uTintColor.value.set(statusColor[0], statusColor[1], statusColor[2]);
  }

  // Update inner cube material
  if (innerMaterial) {
    innerMaterial.opacity = opacity;
    innerMaterial.emissiveIntensity = emissiveIntensity;
  }

  // Update scale
  node.scale.setScalar(scale);
}

/**
 * Highlight edges from a waiting cortex to its dependencies
 */
function highlightDependencyEdges(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  cortexId: string,
  waitingFor: string[]
): void {
  // Find edges from this cortex to its dependencies
  const edges = state.edges;
  if (edges.length === 0) return;

  const edgeLines = edges[0] as THREE_NS.LineSegments;
  const material = edgeLines.material as THREE_NS.LineBasicMaterial;

  // For now, just increase overall edge opacity when waiting
  // In future, could highlight specific edges
  material.opacity = Math.min(0.8, material.opacity + 0.2);
}

export const CORTEX_DESCRIPTIONS: Record<string, string> = {
  conversational: 'The human-facing voice that receives input and provides natural language responses.',
  intent: 'Determines the user\'s goal and decision scope from their input.',
  ttrp: 'Detects if the user\'s stated problem is their actual problem, or if there\'s a misalignment. Prevents "Oh Actually!" moments by diagnosing the real problem before jumping to solutions.',
  meta: 'Analyzes the goal behind the goal — identifies recurring patterns, long-term motivations, and meta-objectives underlying the user\'s current reasoning.',
  objectivity: 'Determines if a problem can be solved objectively (with data, statistics, filtering, sorting, calculation) rather than through LLM reasoning. Critical gate that separates deterministic truth from subjective judgment.',
  survey: 'Gathers baseline context and user circumstances before analysis.',
  context: 'Maintains environmental and conversational context throughout the session.',
  memory: 'Retrieves long-term stored knowledge and past interactions.',
  activation_criteria: 'Evaluates system readiness and determines which cortices should activate.',
  framework: 'Discovers decision-making methodologies and structured approaches.',
  heuristic: 'Generates options, strategies, and creative solutions.',
  empirical: 'Verifies facts, pulls datasets, and validates information.',
  rational: 'Performs logical evaluation and structured reasoning.',
  predictive: 'Models outcomes, scenarios, and ripple effects.',
  reflective: 'Meta-level self-critique and quality assurance.',
  emotional: 'Considers emotional factors and human sentiment.',
  pragmatic: 'Evaluates practical feasibility and real-world constraints.',
  simulation: 'Runs scenarios and models different decision paths.',
  quantitative: 'Performs numerical analysis and data-driven evaluation.',
  social: 'Considers social dynamics and interpersonal factors.',
  ethical: 'Evaluates ethical implications and moral considerations.',
  meta_temporal: 'Considers temporal aspects and time-based reasoning.',
  decision_criteria: 'Defines and evaluates decision criteria and priorities.',
  data: 'The unified gateway to external data sources via the Data API. Queries hundreds of APIs and MCP servers for structured data, web search, integrations, etc. Currently focuses on structured data queries, but scope includes all Data API capabilities.',
  proactive: 'Intelligently reasons about when and why to proactively contact users, evaluating appropriateness of reminder timing and balancing caring vs. annoying.',
  integrator: 'Synthesizes all cortex outputs into a coherent decision graph.',
  decision: 'Produces the final verdict and recommendation.',
  tabular_analysis: 'Analyzes tabular data, discovers patterns, and generates professional insights. Computes comprehensive statistics, identifies anomalies, and assesses data quality.',
  tabular_transform: 'Converts natural language requests into SQL queries with highlight metadata. Creates visualizable transformation profiles (step-by-step operations).',
  tabular_qa: 'Answers questions about datasets using natural language. Generates SQL queries when needed to answer or visualize answers.',

  // ⚙️ DUMMY CORTICES (for stress testing)
  ...Object.fromEntries(
    Array.from({ length: DUMMY_CORTEX_COUNT }, (_, i) => [
      `dummy_${i + 1}`,
      `Dummy cortex ${i + 1} for performance stress testing. This cortex does not perform any actual reasoning.`
    ])
  ),

  // Inactive cortices descriptions
  honesty: 'Detects self-deception, cognitive biases, and truthfulness in user inputs.',
  identity: 'Understands and maintains user identity, values, and self-concept.',
  wellbeing: 'Holistic wellness and life satisfaction optimization.',
  happiness: 'Optimizes for long-term happiness and life satisfaction.',
  quantified_self: 'Data-driven self-optimization and personal analytics.',
  motivation: 'Understands and enhances user motivation, drive, and goal pursuit.',
  therapy: 'Provides therapeutic support, emotional processing, and mental health guidance.',
  fitness: 'Health and fitness decision support.',
  sleep: 'Sleep optimization and sleep-related decisions.',
  nutrition: 'Dietary and nutrition decision support.',
  behavior_addiction: 'Understands behavioral patterns, addictions, and habit change support.',
  financial_advisor: 'Comprehensive financial planning, budgeting, and money management.',
  economics: 'Economic analysis and market understanding.',
  business: 'Business and entrepreneurship decision support.',
  investor: 'Investment analysis, portfolio management, and financial decision support.',
  negotiator_contract: 'Creates agreements with users for subscription discounts based on completed decisions.',
  negotiator_diplomat: 'Real-time meeting assistance providing strategic guidance to achieve user goals.',
  educational: 'Learning and education decision support.',
  fullstack_education: 'Teaches software development through structured learning paths.',
  career_services: 'Career development and professional growth.',
  genius: 'High-level strategic thinking and breakthrough insights.',
  first_principles: 'Breaks down problems to fundamental truths and rebuilds from scratch.',
  better_idea: 'Generates alternative options beyond user\'s initial suggestions.',
  thinking_outside_box: 'Generates unconventional, creative, and non-obvious solutions.',
  contrarian: 'Challenges assumptions, questions consensus, and provides alternative viewpoints.',
  drawing: 'Supports geometry, art, and visual creation on a canvas.',
  software_development: 'Technical decision support for software development and engineering.',
  project_management: 'Supports project planning, product strategy, and execution decisions.',
  entrepreneur: 'Decision support specifically for entrepreneurs and solo indie developers.',
  operating_system: 'Detects and understands user\'s operating system context for technical decisions.',
  web_browser: 'Understands user\'s browser context for web-related decisions.',
  support: 'Supports other cortices when they get confused, encounter errors, or need feedback.',
  evolution: 'Meta-analysis cortex that analyzes system performance to improve Pivot itself.',
  trends: 'Identifies and analyzes trends relevant to user decisions.',
  parenting: 'Parenting decision support and child development guidance.',
  privacy: 'Privacy and security decision support.',
  culture: 'Pays attention to cultural and religious doctrine, holidays, and traditions.',
  proactive_caring: 'Intelligently reasons about when and why to proactively contact users for health reminders, screen dimming, bedtime prep, and well-being check-ins. Context-aware reminder timing that respects user state and preferences.',
  proactive_schedule: 'Proactively reminds users about meetings, deadlines, and time-based rules. Evaluates appropriateness of reminders given user context and schedule conflicts.',
  proactive_insights: 'Proactively shares discoveries, pattern detections, and learning opportunities with users. Identifies interesting findings and relevant insights to share.',
  proactive_wellbeing: 'Monitors life balance, stress levels, and quality of life. Proactively suggests interventions and well-being optimizations based on user patterns and goals.',
};

/**
 * Create the cortex network visualization
 */
export function createCortexNetwork(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS,
  renderer?: THREE_NS.WebGLRenderer,
  camera?: THREE_NS.Camera
): CortexNetworkState {
  const nodes = new Map<string, THREE_NS.Mesh>();
  const edges: THREE_NS.Line[] = [];

  // Simple black cube for inner geometry (simplified from complex primitive system)
  function getInnerCubeColor(cortexId: string): number {
    return 0x000000; // Black
  }

  // Simple box geometry for inner cube (simplified from complex primitive system)
  function getInnerCubeGeometry(cortexId: string, baseSize: number, THREE: typeof THREE_NS): THREE_NS.BufferGeometry {
    return new THREE.BoxGeometry(baseSize, baseSize, baseSize);
  }

  // Create node meshes as glass cubes
  const cubeSize = 0.5 * NETWORK_SCALE;
  const nodeGeometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);

  // Color ranges for each category (variations within each category)
  const getCategoryColor = (category: string, index: number, total: number): { color: number; emissive: number } => {
    const hueVariation = (index / total) * 0.15; // Small variation within category

    switch (category) {
      case 'foundation':
        // Cyan/blue range: base hue ~200, variations
        const foundationHue = 200 + hueVariation * 30;
        return {
          color: new THREE.Color().setHSL(foundationHue / 360, 0.8, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(foundationHue / 360, 0.9, 0.3).getHex(),
        };
      case 'research':
        // Purple/indigo range: base hue ~260
        const researchHue = 260 + hueVariation * 20;
        return {
          color: new THREE.Color().setHSL(researchHue / 360, 0.7, 0.55).getHex(),
          emissive: new THREE.Color().setHSL(researchHue / 360, 0.8, 0.35).getHex(),
        };
      case 'analysis':
        // Magenta/pink range: base hue ~320, variations
        const analysisHue = 320 + hueVariation * 40;
        return {
          color: new THREE.Color().setHSL(analysisHue / 360, 0.75, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(analysisHue / 360, 0.85, 0.3).getHex(),
        };
      case 'integration':
        // Green/teal range: base hue ~160
        const integrationHue = 160 + hueVariation * 20;
        return {
          color: new THREE.Color().setHSL(integrationHue / 360, 0.8, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(integrationHue / 360, 0.9, 0.35).getHex(),
        };
      case 'interface':
        // Golden/amber range: base hue ~40
        return {
          color: 0xffaa00,
          emissive: 0xff6600,
        };
      case 'health':
        // Teal/cyan range: base hue ~180
        const healthHue = 180 + hueVariation * 20;
        return {
          color: new THREE.Color().setHSL(healthHue / 360, 0.7, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(healthHue / 360, 0.8, 0.3).getHex(),
        };
      case 'financial':
        // Green range: base hue ~120
        const financialHue = 120 + hueVariation * 20;
        return {
          color: new THREE.Color().setHSL(financialHue / 360, 0.7, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(financialHue / 360, 0.8, 0.3).getHex(),
        };
      case 'education':
        // Orange range: base hue ~30
        const educationHue = 30 + hueVariation * 20;
        return {
          color: new THREE.Color().setHSL(educationHue / 360, 0.7, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(educationHue / 360, 0.8, 0.3).getHex(),
        };
      case 'technical':
        // Blue range: base hue ~220
        const technicalHue = 220 + hueVariation * 20;
        return {
          color: new THREE.Color().setHSL(technicalHue / 360, 0.7, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(technicalHue / 360, 0.8, 0.3).getHex(),
        };
      case 'specialized':
        // Yellow/gold range: base hue ~50
        const specializedHue = 50 + hueVariation * 30;
        return {
          color: new THREE.Color().setHSL(specializedHue / 360, 0.7, 0.5).getHex(),
          emissive: new THREE.Color().setHSL(specializedHue / 360, 0.8, 0.3).getHex(),
        };
      default:
        return { color: 0xffffff, emissive: 0x888888 };
    }
  };

  // Initialize render target variables (will be set up later when renderer is available)
  let backgroundRenderTarget: THREE_NS.WebGLRenderTarget | null = null;
  let backgroundScene: THREE_NS.Scene | null = null;

  // Count nodes per category for color variation
  const categoryCounts = new Map<string, number>();
  CORTEX_NODES.forEach(n => {
    categoryCounts.set(n.category, (categoryCounts.get(n.category) || 0) + 1);
  });
  const categoryIndices = new Map<string, number>();

  // Create a dummy texture for shader initialization
  const { Texture, Matrix4, Vector2 } = THREE as any;
  const dummyTexture = new Texture();
  dummyTexture.needsUpdate = true;

  for (const node of CORTEX_NODES) {
    const categoryIndex = categoryIndices.get(node.category) || 0;
    categoryIndices.set(node.category, categoryIndex + 1);

    const colors = getCategoryColor(node.category, categoryIndex, categoryCounts.get(node.category) || 1);

    // Convert color to RGB for shader (with reduced saturation)
    const categoryColor = new THREE.Color(colors.color);
    // Desaturate the color
    const hsl = { h: 0, s: 0, l: 0 };
    categoryColor.getHSL(hsl);
    hsl.s *= 0.3; // Reduce saturation to 30%

    // For inactive cortices, make them much dimmer and more desaturated
    if (!node.active) {
      hsl.s *= 0.3; // Even more desaturated (9% of original)
      hsl.l *= 0.4; // Much darker (40% of original brightness)
    }

    const desaturatedColor = new THREE.Color().setHSL(hsl.h, hsl.s, hsl.l);
    const tintRGB = [desaturatedColor.r, desaturatedColor.g, desaturatedColor.b];

    // Create prism shader material with refractive texture mapping
    const { ShaderMaterial, Vector3, DoubleSide } = THREE as any;
    const fragmentShader = createPrismFragmentShader();

    const material = new ShaderMaterial({
      vertexShader: prismVertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        uTintColor: { value: new Vector3(...tintRGB) },
        uTime: { value: 0 },
        uCameraPosition: { value: new Vector3(0, 0, 0) },
        uBackgroundTexture: { value: dummyTexture }, // Will be updated when render target is ready
        uResolution: { value: new Vector2(window.innerWidth, window.innerHeight) },
        uProjectionMatrix: { value: new Matrix4() },
        uViewMatrix: { value: new Matrix4() },
      },
      transparent: true,
      side: DoubleSide,
    });

    const mesh = new THREE.Mesh(nodeGeometry, material);
    mesh.position.set(...node.position);

    // Create simple black inner cube at 0.5 scale relative to parent
    const innerCubeSize = cubeSize * 0.5; // 50% of the outer cube size
    const innerCubeGeometry = getInnerCubeGeometry(node.id, innerCubeSize, THREE);

    // Create inner cube material - transparent black cube with emission for visibility
    const innerCubeColor = getInnerCubeColor(node.id);
    const innerMaterial = new THREE.MeshStandardMaterial({
      color: innerCubeColor,
      emissive: 0x555555, // Brighter gray emission so it shows up better in dark themes
      emissiveIntensity: 1.0, // Strong emission for visibility
      transparent: true,
      opacity: 0.7, // More visible - increased from 0.4 so cubes don't look empty
      metalness: 0.0,
      roughness: 0.5,
      side: THREE.DoubleSide,
      depthWrite: false, // Don't write to depth buffer to help with transparency
    });

    const innerCube = new THREE.Mesh(innerCubeGeometry, innerMaterial);
    // Position at same world position as parent (not as child)
    innerCube.position.set(...node.position);
    // 0.5 scale relative to parent (no additional scaling)
    innerCube.scale.set(1.0, 1.0, 1.0);
    // Set render order to render after outer cube (helps with transparency)
    innerCube.renderOrder = 1;
    // Add directly to scene, not as child
    scene.add(innerCube);

    // Make inactive cortices less visible
    if (!node.active) {
      material.uniforms.uTintColor.value.multiplyScalar(0.3); // Much dimmer
      innerMaterial.opacity = 0.25; // Much more transparent for inactive
      innerMaterial.emissiveIntensity = 0.4; // Dimmer emission for inactive
      mesh.scale.multiplyScalar(0.8); // Slightly smaller
    }

    // Generate unique animation parameters for each node (for variation)
    const nodeIdHash = node.id.split('').reduce((acc, char) => {
      return ((acc << 5) - acc) + char.charCodeAt(0);
    }, 0);

    mesh.userData = {
      cortexId: node.id,
      label: node.label,
      wave: node.wave,
      category: node.category,
      active: node.active, // Store active status
      baseColor: colors.color,
      baseEmissive: colors.emissive,
      tintRGB: tintRGB, // Store for updates
      innerCube: innerCube, // Store reference to inner cube
      innerMaterial: innerMaterial, // Store reference to inner cube material
      // Animation data for glowing particle
      particlePhase: (nodeIdHash % 100) / 100 * Math.PI * 2, // Unique starting phase
      particleSpeed: 0.5 + (Math.abs(nodeIdHash % 50) / 50) * 0.5, // 0.5-1.0 cycles per second
      floatOffset: (nodeIdHash % 20) / 20 * 0.1 * NETWORK_SCALE, // Small vertical float variation
    };

    // Create FAA aircraft warning light particle sprite inside the cube
    // FAA medium-intensity obstruction lights are typically red (L-864/L-865)
    // High-intensity lights are white (L-856/L-857)
    // Using red for medium-intensity style (most common)
    const { Sprite, SpriteMaterial, CanvasTexture } = THREE as any;

    // Create FAA red obstruction light texture (bright, high visibility)
    const canvas = document.createElement('canvas');
    canvas.width = 128; // Higher resolution for better visibility
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // FAA red color: RGB(255, 0, 0) or similar bright red
      // Create bright radial gradient for high visibility
      const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      gradient.addColorStop(0, 'rgba(255, 50, 50, 1.0)'); // Bright red-white center
      gradient.addColorStop(0.2, 'rgba(255, 0, 0, 1.0)'); // Pure red
      gradient.addColorStop(0.5, 'rgba(200, 0, 0, 0.9)'); // Darker red
      gradient.addColorStop(0.8, 'rgba(150, 0, 0, 0.5)'); // Fading red
      gradient.addColorStop(1, 'rgba(100, 0, 0, 0)'); // Transparent edge

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 128, 128);
    }

    const particleTexture = new CanvasTexture(canvas);
    particleTexture.needsUpdate = true;

    const particleMaterial = new SpriteMaterial({
      map: particleTexture,
      color: node.active ? 0xff0000 : 0x333333, // FAA red for active, dark gray for inactive
      transparent: true,
      opacity: node.active ? 1.0 : 0.2, // Much dimmer for inactive
      blending: (THREE as any).AdditiveBlending, // Bright glowing effect
      depthWrite: false,
    });

    const particle = new Sprite(particleMaterial);
    const particleSize = 0.5 * NETWORK_SCALE; // Larger for better visibility
    particle.scale.set(particleSize, particleSize, 1);
    particle.position.set(0, 0, 0); // Center of the cube
    mesh.add(particle);

    nodes.set(node.id, mesh);
    scene.add(mesh);
  }

  // Create clean edge lines (simple lines, not cylinders)
  const edgeGeometry = new THREE.BufferGeometry();
  const edgePositions: number[] = [];
  const edgeColors: number[] = [];

  for (const edge of CORTEX_EDGES) {
    const fromNode = CORTEX_NODES.find(n => n.id === edge.from);
    const toNode = CORTEX_NODES.find(n => n.id === edge.to);

    if (!fromNode || !toNode) continue;

    // Offset line start/end from node centers to avoid overlap
    const from = new THREE.Vector3(...fromNode.position);
    const to = new THREE.Vector3(...toNode.position);
    const direction = to.clone().sub(from).normalize();
    const nodeRadius = 0.35 * NETWORK_SCALE;

    // Pull line endpoints away from node centers
    const start = from.clone().add(direction.clone().multiplyScalar(nodeRadius * 1.1));
    const end = to.clone().sub(direction.clone().multiplyScalar(nodeRadius * 1.1));

    edgePositions.push(
      start.x, start.y, start.z,
      end.x, end.y, end.z
    );

    // Color based on edge type - cleaner palette
    const edgeColor = edge.type === 'required'
      ? new THREE.Color(0x00ff88) // Neon green for required
      : new THREE.Color(0x00aaff); // Cyan for optional
    edgeColors.push(
      edgeColor.r, edgeColor.g, edgeColor.b,
      edgeColor.r, edgeColor.g, edgeColor.b
    );
  }

  edgeGeometry.setAttribute('position', new THREE.Float32BufferAttribute(edgePositions, 3));
  edgeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(edgeColors, 3));

  const edgeMaterial = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.4,
    linewidth: 1,
  });

  const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial);
  scene.add(edgeLines);
  edges.push(edgeLines);

  // Map to store sprites for each edge
  const edgeSprites = new Map<number, THREE_NS.Sprite>();

  // Load sprite texture for edge sprites (same as network visualization)
  let spriteTexture: THREE_NS.Texture | null = null;

  // Create state object first so we can update it
  const networkState: CortexNetworkState = {
    nodes,
    edges,
    edgeSprites,
    pulseState: {
      activeEdge: -1,
      pulseProgress: 0,
      flowIndex: 0,
    },
    raycaster: new THREE.Raycaster(),
    tooltipElement: null as any, // Will be set below
    hoveredNode: null,
    selectedNode: null,
    cameraState: {
      isZoomed: false,
      targetPosition: null,
      originalCameraPosition: null,
      originalCameraTarget: null,
      originalCameraFov: null,
      originalCameraHeight: null,
      zoomStartTime: null,
      zoomStartPosition: null,
      zoomStartTarget: null,
      orbitStartTime: null,
      transitionToOrbitStartTime: null,
      transitionToOrbitStartPosition: null,
    },
    backgroundRenderTarget,
    metaballBackgroundRenderTarget: null, // Will be initialized when renderer is available
    backgroundScene,
    spriteTexture: null,
  };

  import('../network/spriteLoader.js').then(({ loadSpriteTexture }) => {
    loadSpriteTexture(THREE).then((texture) => {
      spriteTexture = texture;
      networkState.spriteTexture = texture; // Update state
      // Update existing sprites with the loaded texture
      edgeSprites.forEach((sprite) => {
        const material = sprite.material as THREE_NS.SpriteMaterial;
        if (material) {
          material.map = texture;
          material.blending = (THREE as any).AdditiveBlending;
          material.needsUpdate = true;
        }
      });
    });
  });

  // Create pulsing effect along edges
  networkState.pulseState = {
    activeEdge: -1,
    pulseProgress: 0,
    flowIndex: 0,
  };

  // Create raycaster for mouse hover detection
  networkState.raycaster = new THREE.Raycaster();

  // Create tooltip element with design system styling
  const tooltipElement = document.createElement('div');
  tooltipElement.style.cssText = `
    position: fixed;
    pointer-events: none;
    z-index: 10000;
    background: rgba(30, 32, 40, 0.95);
    color: #ffffff;
    padding: 8px 12px;
    border-radius: 4px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    line-height: 1.4;
    max-width: 200px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: none;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  `;
  document.body.appendChild(tooltipElement);
  networkState.tooltipElement = tooltipElement;

  // Subscribe to real-time status updates
  const unsubscribe = cortexStatusManager.onStatusUpdate((cortexId, status) => {
    updateNodeVisualState(networkState, THREE, cortexId, status);
  });

  // Store unsubscribe function for cleanup (if needed in future)
  (networkState as any).statusUnsubscribe = unsubscribe;

  // Create vertical rings at cortex height levels
  // Find unique Y levels from active cortices
  const activeCortices = CORTEX_NODES.filter(node => node.active);
  const yLevels = Array.from(new Set(activeCortices.map(node => node.position[1])))
    .sort((a, b) => a - b); // Sort from lowest to highest

  // Create 4 rings at key levels (lowest, second, third, highest)
  if (yLevels.length >= 4) {
    // Pick 4 evenly distributed levels
    const ringLevels = [
      yLevels[0],           // Lowest (smallest ring)
      yLevels[Math.floor(yLevels.length / 3)],  // Second level
      yLevels[Math.floor(yLevels.length * 2 / 3)], // Third level
      yLevels[yLevels.length - 1]  // Highest (largest ring)
    ];

    // Ring sizes: progressively larger, but much smaller than before
    // Based on cortex spread (cortices are roughly -8 to +8 * NETWORK_SCALE in X/Z)
    // So rings should be around 10-14 * NETWORK_SCALE to just enclose them
    const ringSizes = [
      NETWORK_SCALE * 10,   // Smallest (lowest level)
      NETWORK_SCALE * 11,   // Second
      NETWORK_SCALE * 12,   // Third
      NETWORK_SCALE * 13    // Largest (highest level)
    ];

    // Ring colors: brighter gradient from bottom to top for better visibility
    const ringColors = [0x8888cc, 0x9999dd, 0xaaaaff, 0xbbbbff];

    ringLevels.forEach((yLevel, index) => {
      const ring = createVerticalRing(THREE, ringSizes[index], ringColors[index], yLevel);
      scene.add(ring);
    });
  }

  return networkState;
}

/**
 * Create a vertical ring at a specific Y height, centered at origin (0, 0) on X and Z axes
 */
function createVerticalRing(
  THREE: typeof THREE_NS,
  radius: number,
  color: number,
  yHeight: number
): THREE_NS.LineLoop {
  const segments = 128;
  const positions = new Float32Array((segments + 1) * 3);

  for (let i = 0; i <= segments; i += 1) {
    const theta = (i / segments) * Math.PI * 2;
    const x = Math.cos(theta) * radius;
    const z = Math.sin(theta) * radius;
    positions[i * 3] = x;
    positions[i * 3 + 1] = yHeight;
    positions[i * 3 + 2] = z;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const material = new THREE.LineBasicMaterial({
    color,
    transparent: true,
    opacity: 0.5, // Increased opacity for better visibility
    depthWrite: false,
    depthTest: true,
    linewidth: 2 // Thicker lines for better visibility
  });
  const line = new THREE.LineLoop(geometry, material);
  line.renderOrder = 100; // Render after most objects but before UI
  return line;
}

/**
 * Handle mouse click for cortex nodes (zoom and show detailed info)
 */
export function handleCortexNetworkClick(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  mouseX: number,
  mouseY: number,
  camera: THREE_NS.PerspectiveCamera,
  canvas: HTMLCanvasElement
): boolean {

  if (!state.raycaster || !state.tooltipElement) {
    return false;
  }

  // Convert mouse coordinates to normalized device coordinates
  const rect = canvas.getBoundingClientRect();
  const x = ((mouseX - rect.left) / rect.width) * 2 - 1;
  const y = -((mouseY - rect.top) / rect.height) * 2 + 1;

  // Update raycaster
  state.raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

  // Get all cortex nodes as an array
  const nodeMeshes = Array.from(state.nodes.values());

  // Find intersections
  const intersects = state.raycaster.intersectObjects(nodeMeshes);

  if (intersects.length > 0) {
    const intersectedNode = intersects[0].object as THREE_NS.Mesh;
    const cortexId = intersectedNode.userData.cortexId;
    const cortexLabel = intersectedNode.userData.label;
    const distance = intersects[0].distance;

    if (state.selectedNode === cortexId) {
      // Clicking same node - zoom out
      // Emit unfocus event for metaball
      import('../../../core/events.js').then(({ bus }) => {
        bus.emit('cortex.unfocused', {
          cortexId,
          label: cortexLabel,
          timestamp: Date.now()
        });
      }).catch(() => {
        // Event bus not available, ignore
      });

      state.selectedNode = null;
      state.cameraState.isZoomed = false;

      // Store current camera state as starting point for smooth zoom out
      state.cameraState.zoomStartTime = performance.now();
      state.cameraState.zoomStartPosition = camera.position.clone();

      // Calculate current look-at target from camera's forward direction
      const forward = new THREE.Vector3(0, 0, -1);
      forward.applyQuaternion(camera.quaternion);
      const lookAtDistance = 50;
      state.cameraState.zoomStartTarget = camera.position.clone().add(forward.clone().multiplyScalar(lookAtDistance));

      return true;
    } else {
      // Zoom in to new node (or switch to different node)
      // Always update the modal when selecting a new node, even if already zoomed
      const previousSelected = state.selectedNode;

      // Emit unfocus event if there was a previous selection
      if (previousSelected) {
        import('../../../core/events.js').then(({ bus }) => {
          const prevNode = Array.from(state.nodes.values()).find(n => n.userData.cortexId === previousSelected);
          const prevLabel = prevNode?.userData.label || previousSelected;
          bus.emit('cortex.unfocused', {
            cortexId: previousSelected,
            label: prevLabel,
            timestamp: Date.now()
          });
        }).catch(() => {
          // Event bus not available, ignore
        });
      }

      // Emit focus event for new cortex
      import('../../../core/events.js').then(({ bus }) => {
        bus.emit('cortex.focused', {
          cortexId,
          label: cortexLabel,
          timestamp: Date.now()
        });
      }).catch(() => {
        // Event bus not available, ignore
      });

      state.selectedNode = cortexId;
      state.cameraState.isZoomed = true;
      state.cameraState.targetPosition = intersectedNode.position.clone();


      // Store current camera state as starting point for smooth transition
      state.cameraState.zoomStartTime = performance.now();
      state.cameraState.zoomStartPosition = camera.position.clone();

      // Calculate current look-at target from camera's forward direction
      const forward = new THREE.Vector3(0, 0, -1);
      forward.applyQuaternion(camera.quaternion);
      const lookAtDistance = 50; // Approximate distance to look-at point
      state.cameraState.zoomStartTarget = camera.position.clone().add(forward.clone().multiplyScalar(lookAtDistance));

      // Store original camera state if not already stored (for zoom out)
      if (!state.cameraState.originalCameraPosition) {
        state.cameraState.originalCameraPosition = camera.position.clone();
        // For zoom out, we'll look at the origin (where idle camera typically looks)
        state.cameraState.originalCameraTarget = new THREE.Vector3(0, 0, 0);
        state.cameraState.originalCameraFov = camera.fov;
        state.cameraState.originalCameraHeight = camera.position.y; // Preserve original height
      }

      return true;
    }
  }

  return false;
}

/**
 * Show expanded modal with detailed cortex information
 */
function showExpandedCortexModal(state: CortexNetworkState, cortexId: string): void {
  if (!state.tooltipElement) return;

  const node = Array.from(state.nodes.values()).find(n => n.userData.cortexId === cortexId);
  if (!node) return;

  const cortexLabel = node.userData.label;
  const description = CORTEX_DESCRIPTIONS[cortexId] || 'No description available.';
  const dependencies = getCortexDependencies(cortexId);
  const cortexNode = CORTEX_NODES.find(n => n.id === cortexId);
  const category = cortexNode?.category || 'unknown';
  const isInactive = !cortexNode?.active;
  const statusColor = isInactive ? '#666' : '#00ffff';
  const statusText = isInactive ? ' | PLANNED' : '';

  // Build dependencies list
  const depsHtml = dependencies.required.length > 0 || dependencies.optional.length > 0
    ? `
      <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(0, 255, 255, 0.2);">
        <div style="font-size: 11px; color: #00ffff; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 1px;">
          Dependencies
        </div>
        ${dependencies.required.length > 0 ? `
          <div style="font-size: 11px; color: #00ff88; margin-bottom: 4px;">
            Required: ${dependencies.required.map(id => CORTEX_NODES.find(n => n.id === id)?.label || id).join(', ')}
          </div>
        ` : ''}
        ${dependencies.optional.length > 0 ? `
          <div style="font-size: 11px; color: #00aaff; margin-bottom: 4px;">
            Optional: ${dependencies.optional.map(id => CORTEX_NODES.find(n => n.id === id)?.label || id).join(', ')}
          </div>
        ` : ''}
      </div>
    `
    : '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(0, 255, 255, 0.2); font-size: 11px; color: #888;">No dependencies</div>';

  state.tooltipElement.innerHTML = `
    <div style="font-weight: 700; margin-bottom: 6px; color: ${statusColor}; text-transform: uppercase; letter-spacing: 1px; font-size: 16px;">
      ${cortexLabel}
    </div>
    <div style="font-size: 11px; color: ${statusColor}; margin-bottom: 8px; opacity: 0.8; font-family: monospace;">
      Category: ${category.toUpperCase()} | Wave: ${cortexNode?.wave || 0}${statusText}
    </div>
    <div style="font-size: 12px; color: #aaffff; line-height: 1.6; margin-bottom: 8px;">
      ${description}
    </div>
    ${depsHtml}
    <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(0, 255, 255, 0.2); font-size: 10px; color: #888; font-style: italic;">
      Click again to zoom out
    </div>
  `;

  state.tooltipElement.style.display = 'block';
  state.tooltipElement.style.maxWidth = '400px';

  // Position based on screen size (mobile vs desktop)
  const isMobile = window.innerWidth < 768;
  if (isMobile) {
    // Mobile: below center
    state.tooltipElement.style.left = '50%';
    state.tooltipElement.style.top = 'auto';
    state.tooltipElement.style.bottom = '20%';
    state.tooltipElement.style.transform = 'translateX(-50%)';
  } else {
    // Desktop: to the right of center
    state.tooltipElement.style.left = '60%';
    state.tooltipElement.style.top = '50%';
    state.tooltipElement.style.bottom = 'auto';
    state.tooltipElement.style.transform = 'translateY(-50%)';
  }
}

/**
 * Handle mouse hover for cortex nodes
 */
export function handleCortexNetworkHover(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  mouseX: number,
  mouseY: number,
  camera: THREE_NS.Camera,
  canvas: HTMLCanvasElement
): void {
  if (!state.raycaster || !state.tooltipElement) return;

  // Don't show hover tooltip if zoomed in
  if (state.cameraState.isZoomed) {
    if (state.hoveredNode !== null) {
      state.hoveredNode = null;
    }
    return;
  }

  // Convert mouse coordinates to normalized device coordinates
  const rect = canvas.getBoundingClientRect();
  const x = ((mouseX - rect.left) / rect.width) * 2 - 1;
  const y = -((mouseY - rect.top) / rect.height) * 2 + 1;

  // Update raycaster
  state.raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

  // Get all cortex nodes as an array
  const nodeMeshes = Array.from(state.nodes.values());

  // Find intersections
  const intersects = state.raycaster.intersectObjects(nodeMeshes);

  if (intersects.length > 0) {
    const intersectedNode = intersects[0].object as THREE_NS.Mesh;
    const cortexId = intersectedNode.userData.cortexId;
    const cortexLabel = intersectedNode.userData.label;

    if (cortexId !== state.hoveredNode) {
      state.hoveredNode = cortexId;

      // Play hover sound (throttled)
      const now = Date.now();
      if (!state.lastHoverSoundTime || now - state.lastHoverSoundTime > 200) {
        state.lastHoverSoundTime = now;
      }

      // Don't show hover tooltip if already zoomed in (expanded modal should stay visible)
      if (state.cameraState.isZoomed) {
        return;
      }

      // Get real-time status from status manager
      const statusData = cortexStatusManager.getStatus(cortexId);
      const isInactiveCortex = !intersectedNode.userData.active;

      // Determine activity state - simple one-line display
      let activityState = 'Idle';
      if (isInactiveCortex) {
        activityState = 'Planned';
      } else if (statusData) {
        switch (statusData.status) {
          case 'pending':
            activityState = 'Pending';
            break;
          case 'waiting':
            activityState = 'Waiting';
            break;
          case 'active':
            activityState = 'Active';
            break;
          case 'blocked':
            activityState = 'Blocked';
            break;
          case 'skipped':
            activityState = 'Skipped';
            break;
          case 'completed':
            activityState = 'Completed';
            break;
          case 'failed':
            activityState = 'Failed';
            break;
          default:
            activityState = 'Idle';
        }
      }

      // Simple one-line tooltip with design system styling
      state.tooltipElement.innerHTML = `
        <div style="
          font-size: 12px;
          color: #ffffff;
          line-height: 1.4;
          font-family: 'Inter', sans-serif;
        ">
          <span style="font-weight: 600;">${cortexLabel}</span>
          <span style="color: rgba(255, 255, 255, 0.6); margin-left: 8px;">${activityState}</span>
        </div>
      `;

      // Position tooltip near mouse cursor
      state.tooltipElement.style.left = `${mouseX + 15}px`;
      state.tooltipElement.style.top = `${mouseY + 15}px`;
      state.tooltipElement.style.display = 'block';
      state.tooltipElement.style.maxWidth = '200px';
    }
  } else {
    if (state.hoveredNode !== null) {
      state.hoveredNode = null;
      // Only hide tooltip if not zoomed in (when zoomed, expanded modal should stay visible)
      if (!state.cameraState.isZoomed) {
        state.tooltipElement.style.display = 'none';
      } else {
        // If zoomed in, the view will be shown via cortex.focused event
        // No need to restore modal content
      }
    }
  }
}

/**
 * Ease-in-out cubic function for smooth transitions
 */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Update camera zoom/orbit when a cortex is selected
 */
export function updateCortexCamera(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  camera: THREE_NS.PerspectiveCamera,
  deltaTime: number,
  cameraRig?: any // Optional camera rig to get current idle position
): void {
  const { cameraState, selectedNode, nodes } = state;

  const ZOOM_DURATION = 1.5; // seconds for zoom transition
  const TRANSITION_TO_ORBIT_DURATION = 0.8; // seconds for smooth transition to orbit
  const ORBIT_RADIUS = 9 * NETWORK_SCALE; // Tripled from 3 * NETWORK_SCALE for telescopic view
  const ORBIT_SPEED = 0.1; // Reduced from 0.3 for slower, more contemplative rotation
  const TELESCOPE_FOV_DIVISOR = 3; // Divide FOV by this for telescopic zoom effect

  if (!cameraState.isZoomed || !selectedNode || !cameraState.targetPosition) {
    // Zoom out if needed - ensure smooth transition by setting zoomStartTime if not already set
    if (cameraState.originalCameraPosition && cameraState.originalCameraTarget) {
      // If we have original state but no zoom start time, start the zoom-out transition now
      if (!cameraState.zoomStartTime) {
        cameraState.zoomStartTime = performance.now();
        cameraState.zoomStartPosition = camera.position.clone();
        // Calculate current look-at target from camera's forward direction
        const forward = new THREE.Vector3(0, 0, -1);
        forward.applyQuaternion(camera.quaternion);
        const lookAtDistance = 50;
        cameraState.zoomStartTarget = camera.position.clone().add(forward.clone().multiplyScalar(lookAtDistance));
      }

      const elapsed = (performance.now() - cameraState.zoomStartTime) / 1000;
      const progress = Math.min(elapsed / ZOOM_DURATION, 1);
      const eased = easeInOutCubic(progress);

      // Get current idle camera position (where it should be now, not where it was)
      // We need to compute what the camera rig would set it to, without actually applying it
      let currentIdlePosition: THREE_NS.Vector3;
      let currentIdleTarget: THREE_NS.Vector3;
      let currentIdleFov: number;

      if (cameraRig) {
        // Temporarily save current camera state
        const savedPosition = camera.position.clone();
        const savedQuaternion = camera.quaternion.clone();
        const savedFov = camera.fov;

        // Let camera rig compute and apply its state
        const currentTime = performance.now() * 0.001;
        cameraRig.update(currentTime);

        // Capture what the rig set
        currentIdlePosition = camera.position.clone();
        currentIdleFov = camera.fov;

        // Calculate look-at target from camera's forward direction
        const forward = new THREE.Vector3(0, 0, -1);
        forward.applyQuaternion(camera.quaternion);
        const lookAtDistance = 50;
        currentIdleTarget = camera.position.clone().add(forward.clone().multiplyScalar(lookAtDistance));

        // Restore camera state
        camera.position.copy(savedPosition);
        camera.quaternion.copy(savedQuaternion);
        camera.fov = savedFov;
        camera.updateProjectionMatrix();
      } else {
        // Fallback to stored original if no camera rig available
        currentIdlePosition = cameraState.originalCameraPosition;
        currentIdleTarget = cameraState.originalCameraTarget;
        currentIdleFov = cameraState.originalCameraFov || 80;
      }

      // Interpolate from current position to current idle position (not stored original)
      const startPos = cameraState.zoomStartPosition || camera.position.clone();
      const endPos = currentIdlePosition;
      const startTarget = cameraState.zoomStartTarget || cameraState.originalCameraTarget;
      const endTarget = currentIdleTarget;

      camera.position.lerpVectors(startPos, endPos, eased);

      const lookAtTarget = new THREE.Vector3();
      lookAtTarget.lerpVectors(startTarget, endTarget, eased);
      camera.lookAt(lookAtTarget);

      // Restore original FOV (interpolate from telescopic back to current idle FOV)
      if (cameraState.originalCameraFov !== null) {
        const telescopicFov = cameraState.originalCameraFov / TELESCOPE_FOV_DIVISOR;
        const currentFov = camera.fov;
        camera.fov = currentFov + (currentIdleFov - currentFov) * eased;
        camera.updateProjectionMatrix();
      }

      // Reset tooltip transform when zooming out
      if (state.tooltipElement) {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
          state.tooltipElement.style.left = '50%';
          state.tooltipElement.style.top = 'auto';
          state.tooltipElement.style.bottom = '20%';
          state.tooltipElement.style.transform = 'translateX(-50%)';
        } else {
          state.tooltipElement.style.left = '60%';
          state.tooltipElement.style.top = '50%';
          state.tooltipElement.style.bottom = 'auto';
          state.tooltipElement.style.transform = 'translateY(-50%)';
        }
      }

      // Reset if transition complete
      if (progress >= 1) {
        cameraState.zoomStartTime = null;
        cameraState.zoomStartPosition = null;
        cameraState.zoomStartTarget = null;
        cameraState.originalCameraPosition = null;
        cameraState.originalCameraTarget = null;
        cameraState.originalCameraFov = null;
        cameraState.originalCameraHeight = null;
        cameraState.orbitStartTime = null;
        // Hide tooltip when fully zoomed out
        if (state.tooltipElement) {
          state.tooltipElement.style.display = 'none';
        }
      }
    }
    return;
  }

  const targetNode = nodes.get(selectedNode);
  if (!targetNode) return;

  const targetPos = targetNode.position.clone();

  // Handle zoom-in transition
  if (cameraState.zoomStartTime) {
    const elapsed = (performance.now() - cameraState.zoomStartTime) / 1000;
    const progress = Math.min(elapsed / ZOOM_DURATION, 1);
    const eased = easeInOutCubic(progress);

    // Interpolate FOV from original to telescopic (divided by 3)
    if (cameraState.originalCameraFov !== null) {
      const targetFov = cameraState.originalCameraFov / TELESCOPE_FOV_DIVISOR;
      camera.fov = cameraState.originalCameraFov + (targetFov - cameraState.originalCameraFov) * eased;
      camera.updateProjectionMatrix();
    }

    // Calculate initial orbit position (static, not animated yet)
    // Preserve original camera height for dynamic vertical perspective
    const orbitHeight = cameraState.originalCameraHeight !== null
      ? cameraState.originalCameraHeight
      : targetPos.y + ORBIT_RADIUS * 0.5; // Fallback to old behavior if height not stored
    const initialOrbitPosition = new THREE.Vector3(
      targetPos.x + ORBIT_RADIUS,
      orbitHeight,
      targetPos.z
    );

    // Interpolate from start position to initial orbit position
    const startPos = cameraState.zoomStartPosition || camera.position.clone();
    camera.position.lerpVectors(startPos, initialOrbitPosition, eased);

    // Interpolate look-at target
    const startTarget = cameraState.zoomStartTarget || targetPos;
    camera.lookAt(new THREE.Vector3().lerpVectors(startTarget, targetPos, eased));

    // Transition complete, start smooth transition to orbiting
    if (progress >= 1) {
      cameraState.zoomStartTime = null;
      cameraState.transitionToOrbitStartTime = performance.now();
      cameraState.transitionToOrbitStartPosition = camera.position.clone();
    }
  } else if (cameraState.transitionToOrbitStartTime) {
    // Smooth transition from static orbit position to animated orbit
    // Continuously track the current animated orbit position (not a fixed target)
    const elapsed = (performance.now() - cameraState.transitionToOrbitStartTime) / 1000;
    const progress = Math.min(elapsed / TRANSITION_TO_ORBIT_DURATION, 1);
    const eased = easeInOutCubic(progress);

    // Calculate current animated orbit position (where it should be now, continuously updated)
    // Preserve original camera height for dynamic vertical perspective
    const orbitHeight = cameraState.originalCameraHeight !== null
      ? cameraState.originalCameraHeight
      : targetPos.y + ORBIT_RADIUS * 0.5; // Fallback to old behavior if height not stored
    const time = performance.now() * 0.001;
    const currentAnimatedOrbitPosition = new THREE.Vector3(
      targetPos.x + Math.cos(time * ORBIT_SPEED) * ORBIT_RADIUS,
      orbitHeight,
      targetPos.z + Math.sin(time * ORBIT_SPEED) * ORBIT_RADIUS
    );

    // Interpolate from current position towards current animated orbit position (continuously updated)
    // Use a dynamic lerp speed that increases as we progress, ensuring smooth transition
    // At the start (eased=0), lerp slowly; at the end (eased=1), lerp quickly to match orbit
    const lerpSpeed = 0.05 + (eased * 0.15); // Start at 0.05, end at 0.2 (matches full orbit lerp speed)
    camera.position.lerp(currentAnimatedOrbitPosition, lerpSpeed);
    camera.lookAt(targetPos);

    // Maintain telescopic FOV during transition
    if (cameraState.originalCameraFov !== null) {
      const targetFov = cameraState.originalCameraFov / TELESCOPE_FOV_DIVISOR;
      camera.fov = targetFov;
      camera.updateProjectionMatrix();
    }

    // Transition complete, start full orbiting
    if (progress >= 1) {
      cameraState.transitionToOrbitStartTime = null;
      cameraState.transitionToOrbitStartPosition = null;
      cameraState.orbitStartTime = performance.now();
    }
  } else {
    // Already fully orbiting, continue smooth orbit
    if (!cameraState.orbitStartTime) {
      cameraState.orbitStartTime = performance.now();
    }

    // Preserve original camera height for dynamic vertical perspective
    const orbitHeight = cameraState.originalCameraHeight !== null
      ? cameraState.originalCameraHeight
      : targetPos.y + ORBIT_RADIUS * 0.5; // Fallback to old behavior if height not stored
    const time = performance.now() * 0.001;
    const desiredPosition = new THREE.Vector3(
      targetPos.x + Math.cos(time * ORBIT_SPEED) * ORBIT_RADIUS,
      orbitHeight,
      targetPos.z + Math.sin(time * ORBIT_SPEED) * ORBIT_RADIUS
    );

    // Smooth orbit movement (always based on current position)
    const orbitLerpSpeed = 0.05;
    camera.position.lerp(desiredPosition, orbitLerpSpeed);
    camera.lookAt(targetPos);

    // Maintain telescopic FOV during orbit
    if (cameraState.originalCameraFov !== null) {
      const targetFov = cameraState.originalCameraFov / TELESCOPE_FOV_DIVISOR;
      camera.fov = targetFov;
      camera.updateProjectionMatrix();
    }
  }

  // Update tooltip position for hover tooltips (not used for expanded modal anymore)
  if (state.tooltipElement && state.tooltipElement.style.display !== 'none') {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      state.tooltipElement.style.left = '50%';
      state.tooltipElement.style.top = 'auto';
      state.tooltipElement.style.bottom = '20%';
      state.tooltipElement.style.transform = 'translateX(-50%)';
    } else {
      state.tooltipElement.style.left = '60%';
      state.tooltipElement.style.top = '50%';
      state.tooltipElement.style.bottom = 'auto';
      state.tooltipElement.style.transform = 'translateY(-50%)';
    }
  }
}

/**
 * Initialize render target for cortex network (called after renderer is ready)
 */
export function initializeCortexNetworkRenderTarget(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  renderer: THREE_NS.WebGLRenderer,
  camera?: THREE_NS.Camera
): void {
  if (state.backgroundRenderTarget) return; // Already initialized

  const { WebGLRenderTarget } = THREE as any;
  const fullWidth = renderer.domElement.width || window.innerWidth;
  const fullHeight = renderer.domElement.height || window.innerHeight;

  // Create render target for glass cubes (without attention sphere)
  // Render at 100% resolution for high quality look
  const resolutionScale = 0.5; // 100% resolution
  const width = Math.floor(fullWidth * resolutionScale);
  const height = Math.floor(fullHeight * resolutionScale);

  state.backgroundRenderTarget = new WebGLRenderTarget(width, height, {
    minFilter: THREE.LinearFilter, // Linear filter for smooth look
    magFilter: THREE.LinearFilter, // Linear filter for smooth look
    format: THREE.RGBAFormat,
  });

  // Create separate render target for metaball (with attention sphere)
  // Render at 50% resolution for performance (refraction distorts anyway, so quality loss is acceptable)
  const metaballResolutionScale = 0.5; // 50% resolution
  const metaballWidth = Math.floor(width * metaballResolutionScale);
  const metaballHeight = Math.floor(height * metaballResolutionScale);
  state.metaballBackgroundRenderTarget = new WebGLRenderTarget(metaballWidth, metaballHeight, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter, // Linear filtering will upscale when sampled
    format: THREE.RGBAFormat,
  });

  // Update all node materials with the render target texture (glass cubes use regular background)
  state.nodes.forEach((node) => {
    const material = node.material as any;
    if (material && material.uniforms && material.uniforms.uBackgroundTexture) {
      material.uniforms.uBackgroundTexture.value = state.backgroundRenderTarget!.texture;
    }
    // Inner cube uses MeshStandardMaterial, no texture updates needed
  });
}

/**
 * Render background scene to texture for refractive mapping (PS2-style)
 */
export function renderBackgroundForRefraction(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  renderer: THREE_NS.WebGLRenderer,
  scene: THREE_NS.Scene,
  camera: THREE_NS.Camera
): void {
  if (!state.backgroundRenderTarget) return;

  // Update render target size if needed (keep at 100% resolution for high quality)
  const fullWidth = renderer.domElement.width || window.innerWidth;
  const fullHeight = renderer.domElement.height || window.innerHeight;
  const resolutionScale = 0.5; // 100% resolution
  const width = Math.floor(fullWidth * resolutionScale);
  const height = Math.floor(fullHeight * resolutionScale);
  if (state.backgroundRenderTarget.width !== width || state.backgroundRenderTarget.height !== height) {
    state.backgroundRenderTarget.setSize(width, height);
  }

  // Update resolution uniform
  state.nodes.forEach((node) => {
    const material = node.material as any;
    if (material && material.uniforms) {
      if (material.uniforms.uResolution) {
        material.uniforms.uResolution.value.set(width, height);
      }
      if (material.uniforms.uProjectionMatrix && camera instanceof THREE.PerspectiveCamera) {
        material.uniforms.uProjectionMatrix.value.copy(camera.projectionMatrix);
      }
      if (material.uniforms.uViewMatrix) {
        material.uniforms.uViewMatrix.value.copy(camera.matrixWorldInverse);
      }
    }
    // Inner cube uses MeshStandardMaterial, no uniform updates needed
  });

  // Render scene to texture (excluding cortex network nodes, but INCLUDING inner cubes)
  // We hide the outer glass cubes to avoid infinite recursion (they would try to render themselves),
  // but we keep the inner cubes visible so they appear refracted through the glass
  // OPTIMIZATION: Cache visibility states and use direct node references instead of expensive scene.traverse()
  const originalAutoClear = renderer.autoClear;
  renderer.autoClear = true;
  renderer.setRenderTarget(state.backgroundRenderTarget);
  renderer.clear();

  // Hide cortex network nodes (outer glass cubes) directly (much faster than scene.traverse)
  // OPTIMIZATION: Use direct node references from Map instead of expensive scene traversal
  const visibilityStates = new Map<THREE_NS.Mesh, boolean>();

  state.nodes.forEach((mesh) => {
    if (mesh) {
      visibilityStates.set(mesh, mesh.visible);
      mesh.visible = false; // Hide outer glass cubes during background render (to avoid recursion)
      // NOTE: Inner cubes remain visible so they appear refracted through the glass
    }
  });

  renderer.render(scene, camera);

  // Restore visibility (much faster than scene.traverse)
  state.nodes.forEach((mesh) => {
    if (mesh) {
      mesh.visible = visibilityStates.get(mesh) ?? true;
      // Inner cubes were never hidden, so no need to restore them
    }
  });

  renderer.setRenderTarget(null);
  renderer.autoClear = originalAutoClear;
}

/**
 * Render background to texture for metaball refraction (includes attention sphere)
 * This is a separate render from the glass cubes background to allow different content
 */
export function renderMetaballBackgroundForRefraction(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  renderer: THREE_NS.WebGLRenderer,
  scene: THREE_NS.Scene,
  camera: THREE_NS.Camera
): void {
  if (!state.metaballBackgroundRenderTarget) return;

  // Render at lower resolution for performance (refraction distorts anyway, so quality loss is acceptable)
  // Use 50% resolution (0.5x scale) - reduces pixel count by 75% (0.5 * 0.5 = 0.25)
  const resolutionScale = 0.5; // 50% resolution
  const fullWidth = renderer.domElement.width || window.innerWidth;
  const fullHeight = renderer.domElement.height || window.innerHeight;
  const width = Math.floor(fullWidth * resolutionScale);
  const height = Math.floor(fullHeight * resolutionScale);

  // Update render target size if needed
  if (state.metaballBackgroundRenderTarget.width !== width || state.metaballBackgroundRenderTarget.height !== height) {
    state.metaballBackgroundRenderTarget.setSize(width, height);
  }

  // Update resolution uniform (use full resolution for correct UV mapping, even though we render at lower res)
  // The texture will be upscaled automatically when sampled (linear filtering)
  state.nodes.forEach((node) => {
    const material = node.material as any;
    if (material && material.uniforms) {
      if (material.uniforms.uResolution) {
        // Use full resolution for UV calculations (texture will be upscaled when sampled)
        material.uniforms.uResolution.value.set(fullWidth, fullHeight);
      }
      if (material.uniforms.uProjectionMatrix && camera instanceof THREE.PerspectiveCamera) {
        material.uniforms.uProjectionMatrix.value.copy(camera.projectionMatrix);
      }
      if (material.uniforms.uViewMatrix) {
        material.uniforms.uViewMatrix.value.copy(camera.matrixWorldInverse);
      }
    }
  });

  // Render scene to texture (excluding cortex network nodes, but INCLUDING inner cubes AND attention sphere)
  const originalAutoClear = renderer.autoClear;
  renderer.autoClear = true;
  renderer.setRenderTarget(state.metaballBackgroundRenderTarget);
  renderer.clear();

  // Hide cortex network nodes (outer glass cubes) directly
  const visibilityStates = new Map<THREE_NS.Mesh, boolean>();

  state.nodes.forEach((mesh) => {
    if (mesh) {
      visibilityStates.set(mesh, mesh.visible);
      mesh.visible = false; // Hide outer glass cubes during background render (to avoid recursion)
    }
  });

  // NOTE: Attention sphere (blue sphere) should be visible during this render
  // It's already visible from the visibility toggle in index.ts

  renderer.render(scene, camera);

  // Restore visibility
  state.nodes.forEach((mesh) => {
    if (mesh) {
      mesh.visible = visibilityStates.get(mesh) ?? true;
    }
  });

  renderer.setRenderTarget(null);
  renderer.autoClear = originalAutoClear;
}

/**
 * Update cortex network animation (pulsing along edges)
 */
export function updateCortexNetwork(
  state: CortexNetworkState,
  THREE: typeof THREE_NS,
  deltaTime: number,
  camera?: THREE_NS.Camera,
  scene?: THREE_NS.Scene
): void {
  const { pulseState, edges, nodes, edgeSprites } = state;
  const spriteTexture = state.spriteTexture; // Get texture from state

  // Get active cortices from status manager (for edge pulsing animation)
  const allStatuses = cortexStatusManager.getAllStatuses();
  const activeCortices = Array.from(allStatuses.entries())
    .filter(([_, status]) => status.status === 'active' || status.status === 'waiting')
    .map(([id]) => id);

  // For edge pulsing, pulse edges from completed to active cortices
  // This provides visual feedback of data flow
  let activeEdgeIndex = -1;
  if (activeCortices.length > 0) {
    // Find edges leading to active cortices
    const activeCortexId = activeCortices[0]; // Use first active cortex
    activeEdgeIndex = CORTEX_EDGES.findIndex(
      e => activeCortices.includes(e.to)
    );
  }

  // Update all edges - highlight active edge
  const pulseSpeed = 0.3; // Speed of pulse along edge
  const pulseDuration = 1.5; // Time each pulse takes
  pulseState.pulseProgress += deltaTime;
  if (pulseState.pulseProgress >= pulseDuration) {
    pulseState.pulseProgress = 0;
  }

  if (edges.length > 0) {
    const edgeLines = edges[0] as THREE_NS.LineSegments;
    const material = edgeLines.material as THREE_NS.LineBasicMaterial;
    const pulsePhase = (pulseState.pulseProgress / pulseDuration) * Math.PI * 2;
    const pulseIntensity = (Math.sin(pulsePhase) + 1) * 0.5;

    // Base opacity for all edges
    material.opacity = 0.4;

    // If we have an active edge, pulse it
    if (activeEdgeIndex >= 0) {
      material.opacity = 0.4 + pulseIntensity * 0.4;

      // Create or update sprite for active edge
      if (!edgeSprites.has(activeEdgeIndex)) {
        // Create new sprite for this edge (same style as network visualization)
        const { Sprite, SpriteMaterial } = THREE as any;
        const spriteMaterial = new SpriteMaterial({
          color: 0x00ffff, // Cyan color (same as network visualization)
          opacity: 1.0, // Brighter - full opacity
          transparent: true,
          sizeAttenuation: true,
          map: spriteTexture || null, // Use circular sprite texture (soul_sprite.png)
          blending: spriteTexture ? (THREE as any).AdditiveBlending : (THREE as any).NormalBlending, // Glowing effect if texture loaded
          depthWrite: false,
        });
        const sprite = new Sprite(spriteMaterial);
        sprite.scale.set(3, 3, 1); // Larger size for visibility - same as network visualization
        edgeSprites.set(activeEdgeIndex, sprite);

        // Update texture when it loads (if not already loaded)
        // The texture is loaded in createCortexNetwork, but if sprites are created before it loads,
        // we need to update them. The state.spriteTexture will be updated when it loads.
        if (!spriteTexture && state.spriteTexture) {
          spriteMaterial.map = state.spriteTexture;
          spriteMaterial.blending = (THREE as any).AdditiveBlending;
          spriteMaterial.needsUpdate = true;
        } else if (!spriteTexture) {
          // If texture still not loaded, wait for it
          import('../network/spriteLoader.js').then(({ loadSpriteTexture }) => {
            loadSpriteTexture(THREE).then((texture) => {
              spriteMaterial.map = texture;
              spriteMaterial.blending = (THREE as any).AdditiveBlending;
              spriteMaterial.needsUpdate = true;
              // Update state for future sprites
              state.spriteTexture = texture;
            });
          });
        }

        // Add to scene (same parent as edge lines)
        const edgeGeometry = edgeLines.geometry as THREE_NS.BufferGeometry;
        const positions = edgeGeometry.attributes.position.array as Float32Array;
        if (activeEdgeIndex * 2 + 1 < positions.length / 3) {
          // Get edge start position
          const startIndex = activeEdgeIndex * 6; // Each edge has 2 points, 3 coords each
          const startX = positions[startIndex];
          const startY = positions[startIndex + 1];
          const startZ = positions[startIndex + 2];
          sprite.position.set(startX, startY, startZ);
        }
        // Add sprite to same parent as edge lines
        if (scene) {
          if (edgeLines.parent) {
            edgeLines.parent.add(sprite);
          } else {
            scene.add(sprite);
          }
        }
      }

      // Update sprite position along the edge
      const sprite = edgeSprites.get(activeEdgeIndex);
      if (sprite && edges.length > 0) {
        const edgeGeometry = edgeLines.geometry as THREE_NS.BufferGeometry;
        const positions = edgeGeometry.attributes.position.array as Float32Array;
        if (activeEdgeIndex * 2 + 1 < positions.length / 3) {
          // Get edge start and end positions
          const startIndex = activeEdgeIndex * 6;
          const endIndex = startIndex + 3;

          const startX = positions[startIndex];
          const startY = positions[startIndex + 1];
          const startZ = positions[startIndex + 2];

          const endX = positions[endIndex];
          const endY = positions[endIndex + 1];
          const endZ = positions[endIndex + 2];

          // Interpolate position based on pulse progress
          const progress = pulseState.pulseProgress / pulseDuration;
          sprite.position.set(
            startX + (endX - startX) * progress,
            startY + (endY - startY) * progress,
            startZ + (endZ - startZ) * progress
          );

          // Update sprite opacity and scale based on pulse
          const spriteMaterial = sprite.material as THREE_NS.SpriteMaterial;
          spriteMaterial.opacity = 0.7 + pulseIntensity * 0.3;
          sprite.scale.setScalar(2 + pulseIntensity * 1);
        }
      }
    }

    // Clean up sprites for edges that are no longer active
    if (scene) {
      for (const [edgeIndex, sprite] of edgeSprites.entries()) {
        if (edgeIndex !== activeEdgeIndex) {
          // Get scene from the edge lines (they're added to scene)
          const edgeLines = edges[0] as THREE_NS.LineSegments;
          if (edgeLines.parent) {
            edgeLines.parent.remove(sprite);
          } else if (scene) {
            scene.remove(sprite);
          }
          (sprite.material as any).dispose();
          edgeSprites.delete(edgeIndex);
        }
      }
    }
  }

  // Update camera position in shader uniforms for all nodes
  if (camera) {
    const cameraPos = camera.position.clone();
    nodes.forEach((node) => {
      const material = node.material as any;
      if (material && material.uniforms && material.uniforms.uCameraPosition) {
        material.uniforms.uCameraPosition.value.copy(cameraPos);
      }
      // Inner cube uses MeshStandardMaterial, no camera position updates needed
    });
  }

  // Update glowing particles inside nodes (brightness based on real-time status)
  // This is O(n) but very lightweight - just updating sprite opacity
  const currentTime = performance.now() * 0.001; // Time in seconds
  nodes.forEach((node) => {
    const particle = node.children.find((child: any) => child.isSprite) as THREE_NS.Sprite | undefined;
    if (particle && node.userData.particlePhase !== undefined) {
      const phase = node.userData.particlePhase;
      const speed = node.userData.particleSpeed || 0.7;
      const floatOffset = node.userData.floatOffset || 0;

      // Get real-time status from status manager
      const nodeId = node.userData.cortexId;
      const statusData = cortexStatusManager.getStatus(nodeId);
      const isActive = statusData && (statusData.status === 'active' || statusData.status === 'waiting' || statusData.status === 'pending');

      // FAA aircraft warning light pattern: 20-30 flashes per minute
      // Flash ON for ~0.15s, OFF for ~2.0s (typical pattern)
      const flashRate = 25; // flashes per minute (FAA standard)
      const flashCycleDuration = 60.0 / flashRate; // ~2.4 seconds per cycle
      const flashOnDuration = 0.15; // Flash ON for 150ms

      let baseOpacity = 0.15; // Dim but visible when inactive
      let scaleMultiplier = 1.0;

      if (isActive) {
        // Calculate flash pattern based on time
        const cycleTime = (currentTime % flashCycleDuration);
        // Sharp on/off pattern (not smooth sine wave)
        if (cycleTime < flashOnDuration) {
          // Flash ON - maximum brightness (FAA high-intensity style)
          baseOpacity = 2.0; // Very bright, can exceed 1.0 for intense glow
          scaleMultiplier = 1.2; // Slightly larger when flashing for visibility
        } else {
          // Flash OFF - dim but visible (not completely off)
          baseOpacity = 0.2; // Slightly brighter than inactive state
          scaleMultiplier = 1.0;
        }
      } else {
        // Inactive nodes - dim but visible
        baseOpacity = 0.15;
        scaleMultiplier = 1.0;
      }

      // Update scale (slightly larger when flashing for visibility)
      const baseScale = 0.5 * NETWORK_SCALE;
      particle.scale.set(baseScale * scaleMultiplier, baseScale * scaleMultiplier, 1);

      // Update opacity based on FAA aircraft warning light pattern
      const particleMaterial = particle.material as THREE_NS.SpriteMaterial;
      particleMaterial.opacity = baseOpacity;

      // Gentle vertical floating (very subtle, slow)
      const floatPhase = (currentTime * speed * 0.1 + phase) * Math.PI * 2;
      const floatAmount = Math.sin(floatPhase) * floatOffset * 0.5; // Reduced float amount
      particle.position.y = floatAmount;
    }
  });

  // Visual updates are now handled by updateNodeVisualState() via status manager callbacks
  // This keeps the code clean and ensures real-time status updates

  // Tooltip updates are handled in handleCortexNetworkHover() with real-time status
}

// Export shader code for reuse (e.g., UI sphere)
export function getPrismShaders(includeLogo: boolean = false) {
  return {
    vertexShader: prismVertexShader,
    fragmentShader: createPrismFragmentShader(includeLogo),
  };
}

